// client/src/pages/FAQ.js
// FAQ page with search and category filtering
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Search } from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQItem from '../components/help/FAQItem';
import SearchBar from '../components/help/SearchBar';
import faqData from '../data/faqData';

const FAQ = () => {
  const { t, i18n } = useTranslation(['help']);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Get FAQs in current language
  const currentLanguage = i18n.language || 'en';
  const faqs = faqData[currentLanguage] || faqData.en;

  // Categories
  const categories = [
    { id: 'all', label: 'All' },
    { id: 'general', label: 'General' },
    { id: 'orders', label: 'Orders' },
    { id: 'products', label: 'Products' },
    { id: 'discounts', label: 'Discounts' },
    { id: 'business', label: 'For Businesses' },
    { id: 'wholesaler', label: 'For Wholesalers' },
    { id: 'account', label: 'Account' },
    { id: 'billing', label: 'Billing' }
  ];

  // Filter and search FAQs
  const filteredFAQs = useMemo(() => {
    let filtered = faqs;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(faq =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [faqs, selectedCategory, searchQuery]);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <HelpCircle className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {currentLanguage === 'el' ? 'Συχνές Ερωτήσεις' : 'Frequently Asked Questions'}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {currentLanguage === 'el' 
              ? 'Γρήγορες απαντήσεις σε συνήθεις ερωτήσεις'
              : 'Quick answers to common questions'}
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery('')}
              placeholder={currentLanguage === 'el' ? 'Αναζήτηση ερωτήσεων...' : 'Search FAQs...'}
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex flex-wrap gap-2 justify-center min-w-max">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        {searchQuery && (
          <div className="mb-4 text-center text-gray-600">
            {currentLanguage === 'el' 
              ? `${filteredFAQs.length} ερωτήσ${filteredFAQs.length === 1 ? 'η' : 'εις'} βρέθηκαν`
              : `${filteredFAQs.length} question${filteredFAQs.length === 1 ? '' : 's'} found`}
          </div>
        )}

        {/* FAQ Items */}
        <div className="mb-8">
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map(faq => (
              <FAQItem
                key={faq.id}
                question={faq.question}
                answer={faq.answer}
              />
            ))
          ) : (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {currentLanguage === 'el' 
                  ? 'Δεν βρέθηκαν ερωτήσεις που να ταιριάζουν με την αναζήτησή σας.'
                  : 'No questions found matching your search.'}
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
              >
                {currentLanguage === 'el' ? 'Εκκαθάριση φίλτρων' : 'Clear filters'}
              </button>
            </div>
          )}
        </div>

        {/* Contact CTA */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {currentLanguage === 'el' 
              ? 'Δεν βρήκατε την απάντησή σας;'
              : 'Can\'t find your answer?'}
          </h2>
          <p className="text-gray-600 mb-6">
            {currentLanguage === 'el' 
              ? 'Η ομάδα υποστήριξής μας είναι εδώ για να βοηθήσει!'
              : 'Our support team is here to help!'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@wholesalehub.gr"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              {currentLanguage === 'el' ? 'Επικοινωνήστε με Υποστήριξη' : 'Contact Support'}
            </a>
            <button
              onClick={() => window.location.href = '/help'}
              className="px-6 py-3 bg-white text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
            >
              {currentLanguage === 'el' ? 'Επισκεφθείτε το Κέντρο Βοήθειας' : 'Visit Help Center'}
            </button>
          </div>
        </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FAQ;